'use client';

import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Recaptcha } from '@/components/ui/Recaptcha';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { RECAPTCHA_SITE_KEY } from '@/lib/const';
import { getTitleFieldLabel } from '@/lib/forms/titleOptions';
import { getCountryFromLocale, getLanguageFromLocale } from '@/lib/i18n/config';
import clsx from 'clsx';
import { useLocale } from 'next-intl';
import type { ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';

import { AppointmentLegalSection } from './AppointmentLegalSection';
import styles from './BookAppointmentModal.module.scss';
import { CalendarBase } from './CalendarBase';
import type { AppointmentFormStepState } from './useAppointmentFormStep';

interface AppointmentFormStepFieldsProps extends AppointmentFormStepState {
  countryCode: string;
  languageCode: string;
  retailerSection?: ReactNode;
}

function FormSection({
  title,
  isPageLayout,
  children,
}: Readonly<{ title: string; isPageLayout: boolean; children: ReactNode }>) {
  return (
    <section className={clsx(styles.formSection, isPageLayout && styles.formSectionPage)}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {children}
    </section>
  );
}

/**
 * Appointment form fields: scheduling, contact info, reason, consent, and submit.
 */
export function AppointmentFormStepFields({
  countryCode,
  languageCode,
  retailerSection,
  t,
  tForms,
  reasonOptions,
  isPageLayout,
  schedulingDisabled,
  tomorrow,
  futureDate,
  selectedDate,
  availableTimes,
  nonWorkingDays,
  submitError,
  isSubmitting,
  captchaToken,
  setCaptchaToken,
  recaptchaRef,
  titleOptions,
  formMethods,
  register,
  onSubmit,
  handleDateChange,
  errors,
  layout,
  boutique,
}: Readonly<AppointmentFormStepFieldsProps>) {
  const locale = useLocale();
  const marketCountry = getCountryFromLocale(locale).toUpperCase();
  const marketLanguage = getLanguageFromLocale(locale);
  const isJP = marketCountry === 'JP';

  const submitDisabled =
    isSubmitting || schedulingDisabled || (Boolean(RECAPTCHA_SITE_KEY) && !captchaToken);

  return (
    <FormProvider {...formMethods}>
      <form
        className={clsx(styles.form, isPageLayout && styles.formPage)}
        onSubmit={onSubmit}
        noValidate
      >
        {retailerSection}

        <FormSection title={t('appointmentDate')} isPageLayout={isPageLayout}>
          <CalendarBase
            value={selectedDate}
            minDate={tomorrow}
            maxDate={futureDate}
            nonWorkingDays={nonWorkingDays}
            unavailableAppointmentWeekdays={boutique?.unavailableWeekdays ?? []}
            disabled={schedulingDisabled}
            layout={layout}
            onChange={handleDateChange}
          />
          <p className={clsx(styles.helperText, isPageLayout && styles.helperTextPage)}>
            {t('sameDayHint')}
          </p>
        </FormSection>

        <FormSection title={t('timeSlot')} isPageLayout={isPageLayout}>
          <Select
            fullWidth
            placeholder={t('timeSlotPlaceholder')}
            options={availableTimes}
            disabled={schedulingDisabled || availableTimes.length === 0}
            error={errors.timeSlot?.message}
            hasError={Boolean(errors.timeSlot)}
            required
            {...register('timeSlot')}
          />
          <p className={clsx(styles.helperText, isPageLayout && styles.helperTextPage)}>
            {t('validAppointmentHint')}
          </p>
        </FormSection>

        <FormSection title={t('personalInfo')} isPageLayout={isPageLayout}>
          <Select
            id="appointment-title"
            aria-label={getTitleFieldLabel(marketCountry, (key) => tForms(key))}
            placeholder={getTitleFieldLabel(marketCountry, (key) => tForms(key))}
            fullWidth
            options={titleOptions}
            error={errors.title?.message}
            hasError={Boolean(errors.title)}
            required
            {...register('title')}
          />

          {isJP ? (
            marketLanguage === 'ja' || marketLanguage === 'zh' ? (
              <>
                <div className={clsx(styles.fieldRow, isPageLayout && styles.fieldRowPage)}>
                  <Input
                    placeholder={tForms('lastNameKanji')}
                    error={errors.lastNameKanji?.message}
                    autoComplete="family-name"
                    required
                    fullWidth
                    {...register('lastNameKanji')}
                  />
                  <Input
                    placeholder={tForms('firstNameKanji')}
                    error={errors.firstNameKanji?.message}
                    autoComplete="given-name"
                    required
                    fullWidth
                    {...register('firstNameKanji')}
                  />
                </div>
                <div className={clsx(styles.fieldRow, isPageLayout && styles.fieldRowPage)}>
                  <Input
                    placeholder={tForms('lastNameForJP')}
                    error={errors.lastName?.message}
                    required
                    fullWidth
                    {...register('lastName')}
                  />
                  <Input
                    placeholder={tForms('firstNameForJP')}
                    error={errors.firstName?.message}
                    required
                    fullWidth
                    {...register('firstName')}
                  />
                </div>
              </>
            ) : (
              <>
                <div className={clsx(styles.fieldRow, isPageLayout && styles.fieldRowPage)}>
                  <Input
                    placeholder={tForms('firstNameForJP')}
                    error={errors.firstName?.message}
                    autoComplete="given-name"
                    required
                    fullWidth
                    {...register('firstName')}
                  />
                  <Input
                    placeholder={tForms('lastNameForJP')}
                    error={errors.lastName?.message}
                    autoComplete="family-name"
                    required
                    fullWidth
                    {...register('lastName')}
                  />
                </div>
                <div className={clsx(styles.fieldRow, isPageLayout && styles.fieldRowPage)}>
                  <Input
                    placeholder={tForms('firstNameKanji')}
                    error={errors.firstNameKanji?.message}
                    required
                    fullWidth
                    {...register('firstNameKanji')}
                  />
                  <Input
                    placeholder={tForms('lastNameKanji')}
                    error={errors.lastNameKanji?.message}
                    required
                    fullWidth
                    {...register('lastNameKanji')}
                  />
                </div>
              </>
            )
          ) : (
            <div className={clsx(styles.fieldRow, isPageLayout && styles.fieldRowPage)}>
              <Input
                placeholder={tForms('firstName')}
                error={errors.firstName?.message}
                required
                fullWidth
                {...register('firstName')}
              />
              <Input
                placeholder={tForms('lastName')}
                error={errors.lastName?.message}
                required
                fullWidth
                {...register('lastName')}
              />
            </div>
          )}

          <Input
            type="email"
            placeholder={tForms('email')}
            autoComplete="email"
            error={errors.email?.message}
            required
            fullWidth
            {...register('email')}
          />

          <PhoneInput
            prefixLabel={`${tForms('prefix')}`}
            phoneLabel={tForms('telephoneNumber')}
            lang={languageCode}
            country={countryCode}
            required
          />

          <p className={styles.mandatoryFields}>{t('mandatoryFields')}</p>
        </FormSection>

        <FormSection title={t('reasonTitle')} isPageLayout={isPageLayout}>
          <Select
            fullWidth
            placeholder={t('reasonPlaceholder')}
            options={reasonOptions.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            error={errors.reason?.message}
            hasError={Boolean(errors.reason)}
            required
            {...register('reason')}
          />

          <Textarea
            rows={4}
            placeholder={t('commentsPlaceholder')}
            error={errors.comments?.message}
            fullWidth
            {...register('comments')}
          />
        </FormSection>

        <AppointmentLegalSection register={register} consentError={errors.consent} />

        {RECAPTCHA_SITE_KEY ? (
          <div className={styles.recaptcha}>
            <Recaptcha ref={recaptchaRef} onChange={(token) => setCaptchaToken(token)} />
          </div>
        ) : null}

        {submitError ? (
          <p className={styles.error} role="alert">
            {submitError}
          </p>
        ) : null}

        <Button
          type="submit"
          variant={isPageLayout ? 'secondary' : 'primary'}
          size={isPageLayout ? 'lg' : 'md'}
          fullWidth
          loading={isSubmitting}
          disabled={submitDisabled}
          iconAfter={
            isPageLayout ? <Icon name="chevron-right" size={16} aria-hidden="true" /> : undefined
          }
          className={isPageLayout ? styles.submitButtonPage : undefined}
        >
          {t('submit')}
        </Button>
      </form>
    </FormProvider>
  );
}

AppointmentFormStepFields.displayName = 'AppointmentFormStepFields';
